import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { FormsModule, Validators } from '@angular/forms';

import { BaseFormField, FieldError } from '@forthbridge/forms';

import { TranslateModule } from '@ngx-translate/core';

import { type HeightInputConfig, type HeightInputState, type HeightValue } from './height-input.types';

@Component({
  selector: 'app-height-input',
  templateUrl: './height-input.html',
  styleUrl: './height-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, FieldError],
})
export class HeightInput extends BaseFormField<HeightValue> {
  readonly config = input<HeightInputConfig>({});
  readonly disabled = input<boolean>(false);

  protected readonly currentState = signal<HeightInputState>('idle');

  protected readonly inputLabel = computed(() => this.config().label || this.label);

  /** Override to handle HeightValue object validation */
  override get invalid(): boolean {
    const ctrl = this.control;
    if (!ctrl) return false;

    // Check if required and both values are empty
    const isRequired = ctrl.hasValidator?.(Validators.required);
    const isEmpty = this.feetValue == null && this.inchesValue == null;
    const hasRequiredError = isRequired && isEmpty;

    // Combine with any other control errors
    const hasErrors = (!!ctrl.errors || hasRequiredError) && (ctrl.dirty || ctrl.touched);

    return !this.isFocused && hasErrors;
  }

  // Internal values for feet and inches
  protected feetValue: number | null = null;
  protected inchesValue: number | null = null;

  override writeValue(value: HeightValue | null | undefined): void {
    super.writeValue(value as HeightValue);
    this.feetValue = value?.feet ?? null;
    this.inchesValue = value?.inches ?? null;
    this.updateState();
  }

  protected onFeetChange(value: number | null): void {
    this.feetValue = value;
    this.syncToParent();
  }

  protected onInchesChange(value: number | null): void {
    this.inchesValue = value;
    this.syncToParent();
  }

  private syncToParent(): void {
    // Send null if both empty (for required validation)
    const value: HeightValue | null =
      this.feetValue == null && this.inchesValue == null ? null : { feet: this.feetValue, inches: this.inchesValue };

    this.syncToControl(value as HeightValue);
    this.updateState();
  }

  override onFocus(): void {
    super.onFocus();
    this.updateState();
  }

  override onBlur(): void {
    super.onBlur();
    this.updateState();
  }

  // Uses input event instead of keypress because input[type="number"] returns null for
  // selectionStart/selectionEnd, which blocked typing over selected text. Trims after entry
  // and re-dispatches to sync ngModel with the corrected value.
  protected enforceMaxLength(event: Event, maxLength: number): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
      input.dispatchEvent(new Event('input'));
    }
  }

  private updateState(): void {
    if (this.isDisabledState) {
      this.currentState.set('disabled');
    } else if (this.invalid) {
      this.currentState.set('error');
    } else if (this.isFocused || this.hasValue()) {
      this.currentState.set('filled');
    } else {
      this.currentState.set('idle');
    }
  }

  protected readonly containerClass = computed(() => {
    return 'flex items-center justify-center';
  });

  protected readonly containerStyle = computed(() => {
    const state = this.currentState();
    return {
      gap: 'var(--measurementinput-gap)',
      paddingLeft: 'var(--measurementinput-padding-x)',
      paddingRight: 'var(--measurementinput-padding-x)',
      paddingTop: 'var(--measurementinput-padding-y)',
      paddingBottom: 'var(--measurementinput-padding-y)',
      borderRadius: 'var(--measurementinput-border-radius)',
      fontFamily: 'var(--measurementinput-font-family)',
      background: `var(--measurementinput-${state}-background)`,
      borderWidth: `var(--measurementinput-${state}-border-width)`,
      borderColor: `var(--measurementinput-${state}-border-color)`,
      borderStyle: 'solid',
    };
  });

  protected readonly placeholderColor = computed(() => {
    const state = this.currentState();
    return `var(--measurementinput-${state}-placeholder-color)`;
  });

  private hasValue(): boolean {
    return this.feetValue != null || this.inchesValue != null;
  }
}
